import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getRadioEpisodes } from '@aron/api';
import RadioEpisode from '@aron/audio/radio-episode/RadioEpisode';
import { RadioEpisode as RadioEpisodeType } from '@aron/api';
import { useTranslation } from 'react-i18next';
import Spinner from '@fec/frontend/elements/loading/spinner/Spinner';
import ShowMoreBar from '@fec/frontend/modules/groups-and-lists/show-more-bar/show-more-bar';
import '@fec/frontend/foundation/translations/react_i18n_init';

import './RadioEpisodes.scss';

const DEFAULT_PAGE_SIZE = 20;

type RadioEpisodesProps = {
  radioShowId: string;
  nextRadioEpisodes: Array<RadioEpisodeType>;
};
const RadioEpisodes = ({
  radioShowId,
  nextRadioEpisodes,
}: RadioEpisodesProps) => {
  const { t } = useTranslation();
  const previewTitleRef = useRef<HTMLHeadingElement>(null);
  const [showAllNextRadioEpisodes, setShowAllNextRadioEpisodes] =
    useState(false);

  const fetchRadioEpisodes = async ({ pageParam }: { pageParam: number }) => {
    return await getRadioEpisodes({
      id: radioShowId,
      page: pageParam,
    });
  };

  const { data, fetchNextPage, hasNextPage, isFetching, status } =
    useInfiniteQuery({
      queryKey: ['radioEpisodes'],
      queryFn: fetchRadioEpisodes,
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages, lastPageParam) => {
        // if last page has fewer episodes than the default page size, there are no more pages
        if (lastPage.length < DEFAULT_PAGE_SIZE) {
          return undefined;
        }

        // return the next page number
        return lastPageParam + 1;
      },
    });

  const radioEpisodes = data?.pages.flatMap((page) => page) ?? [];

  // Focus the first newly loaded radio episode
  const firstNewRadioEpisodeRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (firstNewRadioEpisodeRef.current) {
      firstNewRadioEpisodeRef.current.focus();
    }
  }, [radioEpisodes.length]);

  const nextRadioEpisodesRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (nextRadioEpisodesRef.current) {
      nextRadioEpisodesRef.current.focus();
    }
  }, [showAllNextRadioEpisodes]);

  return (
    <div className="radio-episodes">
      {nextRadioEpisodes.length > 0 && (
        <>
          <h3 ref={previewTitleRef} className="radio-episodes__title">
            {t('radioEpisodes.previewTitle')}
          </h3>
          <ul>
            {nextRadioEpisodes
              .filter(
                (radioEpisode, index) =>
                  showAllNextRadioEpisodes || index === 0,
              )
              .map((radioEpisode, index) => {
                // If only the first radio episode is shown, focus it
                // If all radio episodes are shown, focus the second one
                const isRadioEpisodeFocused =
                  (!showAllNextRadioEpisodes && index === 0) ||
                  (showAllNextRadioEpisodes && index === 1);

                return (
                  <RadioEpisode
                    key={radioEpisode.id}
                    ref={isRadioEpisodeFocused ? nextRadioEpisodesRef : null}
                    radioEpisode={radioEpisode}
                    isPreview={true}
                  />
                );
              })}
          </ul>
          {nextRadioEpisodes.length > 1 && (
            <div className="radio-episodes__show-more-bar">
              <ShowMoreBar
                // @ts-ignore
                showMoreText={
                  showAllNextRadioEpisodes
                    ? t('radioEpisodes.hidePreviews')
                    : t('radioEpisodes.showMorePreviews')
                }
                renderBackToTop={false}
                iconDirection={showAllNextRadioEpisodes ? 'up' : 'down'}
                onClick={() => {
                  setShowAllNextRadioEpisodes(!showAllNextRadioEpisodes);
                  previewTitleRef.current?.scrollIntoView();
                }}
              />
            </div>
          )}
        </>
      )}
      <h3 className="radio-episodes__title">{t('radioEpisodes.title')}</h3>
      {status === 'error' && !isFetching && (
        <div className="radio-episodes__error">{t('radioEpisodes.error')}</div>
      )}
      {radioEpisodes.length > 0 && (
        <ul>
          {radioEpisodes.map((radioEpisode, index) => {
            // Attach the ref to the first newly loaded radio episode
            const numberOfItemsInLastPage =
              data?.pages[data.pages.length - 1].length ?? 0;
            const isFirstNewRadioEpisode =
              index === radioEpisodes.length - numberOfItemsInLastPage;

            return (
              <RadioEpisode
                key={radioEpisode.id}
                ref={isFirstNewRadioEpisode ? firstNewRadioEpisodeRef : null}
                radioEpisode={radioEpisode}
              />
            );
          })}
        </ul>
      )}
      {radioEpisodes.length === 0 && !isFetching && status !== 'error' && (
        <div className="radio-episodes__no-episodes">
          {t('radioEpisodes.noEpisodes')}
        </div>
      )}
      {isFetching && (
        <div className="radio-episodes__loading">
          <Spinner />
        </div>
      )}
      {!isFetching && (
        <div className="radio-episodes__show-more-bar">
          <ShowMoreBar
            // @ts-ignore
            showMoreText={t('radioEpisodes.showMore')}
            showMoreTextMobile={t('radioEpisodes.showMoreMobile')}
            backToTopText={t('radioEpisodes.backToTop')}
            onClick={() => fetchNextPage()}
            hasMore={hasNextPage}
          />
        </div>
      )}
    </div>
  );
};

export default RadioEpisodes;
